import clsx from "clsx";
import { useEffect, useState } from "react";

// "Continue with Google / Microsoft", on the funnel's account card and on
// /login.
//
// Each button is a real form POST, not a fetch: what follows is a full-page
// trip to the provider and back, and the server only starts one from a POST
// carrying Rails' authenticity token (the meta tag the layout renders), so no
// other site can start a sign-in in this browser. The context on the action's
// query string goes into the server's session for the far side of the trip.
// What comes back is the page itself, with either ?oauth=done — the session is
// waiting in this browser's cookie for collectProviderSession() — or an
// ?oauth_error= code (OauthController).
const PROVIDERS = [
  { key: "google", name: "Google", Mark: GoogleMark },
  { key: "microsoft", name: "Microsoft", Mark: MicrosoftMark },
];

// Why a provider sign-in came back without a session. The account card puts
// its own reasons in front of these, because a provider's address goes through
// the same classification a typed one does.
const OAUTH_ERROR: Record<string, string> = {
  UNAVAILABLE: "That sign-in option isn't available yet.",
  UNVERIFIED:
    "We couldn't confirm the email address on that account. Please use your work email instead.",
  NO_ACCOUNT: "There's no account for that address yet.",
  FAILED: "We couldn't sign you in with that account. Please try again.",
};

// The session a provider sign-in left in this browser's cookie, or null when
// there is none (it was collected already, or sat there over a minute). Only
// this browser can collect it, which is the point: nothing in the URL that
// brought the page here can sign anyone in.
export async function collectProviderSession(): Promise<string | null> {
  const response = await fetch("/oauth/handoff", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) return null;
  const body = (await response.json()) as { token?: string };
  return body.token ?? null;
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ??
  "";

export function oauthErrorMessage(code: string): string | null {
  // Backing out on the provider's page is a choice, not a failure.
  if (code === "CANCELLED") return null;
  return OAUTH_ERROR[code] ?? OAUTH_ERROR.FAILED;
}

export default function OauthButtons({
  intent,
  leadToken,
  nextStepKey,
  disabled,
}: {
  intent: "signup" | "login";
  leadToken?: string | null;
  nextStepKey?: string | null;
  disabled?: boolean;
}) {
  // The provider the browser is on its way to. Coming back from that provider's
  // page with the Back button can restore this one from the back/forward cache
  // with it still set, which would leave both buttons dead — so a restore
  // clears it.
  const [leaving, setLeaving] = useState<string | null>(null);
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) setLeaving(null);
    };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);

  const context = new URLSearchParams({ intent });
  if (leadToken) context.set("lead_token", leadToken);
  if (nextStepKey) context.set("next_step_key", nextStepKey);

  return (
    <div className="space-y-3">
      {PROVIDERS.map(({ key, name, Mark }) => (
        <form
          key={key}
          method="post"
          action={`/auth/${key}?${context}`}
          onSubmit={() => setLeaving(key)}
        >
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <button
            type="submit"
            disabled={disabled || leaving !== null}
            className={clsx(
              "flex h-11 w-full items-center justify-center gap-2.5 rounded-lg border border-zinc-300 bg-white text-body font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-default disabled:hover:bg-white",
              leaving && leaving !== key && "opacity-50"
            )}
          >
            <Mark />
            {leaving === key ? `Opening ${name}…` : `Continue with ${name}`}
          </button>
        </form>
      ))}
    </div>
  );
}

function GoogleMark() {
  return (
    <svg aria-hidden viewBox="0 0 48 48" className="h-[18px] w-[18px]">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59a14.4 14.4 0 0 1 0-9.18l-7.97-6.19a24 24 0 0 0 0 21.56l7.97-6.19Z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.9l-7.97 6.19C6.51 42.62 14.62 48 24 48Z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg aria-hidden viewBox="0 0 23 23" className="h-4 w-4">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}
